/**
 * NotificationService.ts
 *
 * 캘린더 알림 및 초대 관리를 담당하는 모듈
 *
 * 주요 기능:
 * - 캘린더 초대 알림 수신 및 처리
 * - 초대 응답 (수락/거절) 처리
 * - 알림 상세 정보 파싱 및 관리
 * - 알림 삭제 및 정리
 * - XML 기반 알림 데이터 처리
 *
 * 이 모듈은 다른 사용자로부터 받은 캘린더 공유 초대를 처리하며,
 * 사용자가 초대에 응답할 수 있도록 합니다.
 */

import * as convert from 'xml-js'

import {
  classInfo,
  notificationEmitter,
  doingFetchNotifications,
  setDoingFetchNotifications,
  getCalendars,
} from './CalendarState'
import { getClient } from './CalendarClient'
import { fetchCalendars, enableSync } from './CalendarSync'
// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail, getDisplayEmailFromPersonaEmail } from 'utils/personaEmail'
import { isOrgSharedAccountEmail } from 'utils/orgSharedAccount'
// 순환 참조 방지를 위해 Settings 팝업 모듈에서 Redux store 를 지연 접근
import settings from 'popups/Settings'
// TodoSync → NotificationService → TodoFolderSync → TodoSync 순환 참조 방지를 위해 dynamic import 사용

// 조직공용계정 초대 자동 수락 중복 방지 Set
const autoAcceptingNotiUrls = new Set<string>()

/**
 * Calendar notification object
 */
export interface CalendarNotification {
  isReply: boolean
  notiUrl: string
  folderType: 'calendar' | 'todo'
  uid?: string
  href?: string
  hostUrl?: string
  organizer?: string
  summary?: string
  replyUrl?: string
  accept?: boolean
}

/**
 * XML response structures
 */
interface XmlAttribute {
  name: string
}

interface XmlText {
  _text: string
}

interface XmlComponent {
  _attributes: XmlAttribute
}

interface XmlInviteNotification {
  uid: XmlText
  href: XmlText
  hosturl: { href: XmlText }
  organizer: { commonName: XmlText }
  summary: XmlText
  supportedCalendarComponentSet: {
    comp: XmlComponent | XmlComponent[]
  }
  access: {
    noAccess?: unknown
  }
}

interface XmlInviteReply {
  href: XmlText
  hosturl: { href: XmlText }
  summary: XmlText
  inviteAccepted?: unknown
}

interface XmlNotificationResult {
  notification: {
    inviteNotification?: XmlInviteNotification
    inviteReply?: XmlInviteReply
  }
}

interface DavResponse {
  ok: boolean
  href?: string
  raw?: string
}

/**
 * Get notification detail from URL
 */
const getNotiDetail = async (
  url: string,
  completeCallback: (notifications: CalendarNotification[]) => void = (_notifications) => {},
): Promise<void> => {
  const client = getClient()
  if (!client) {
    completeCallback([])
    return
  }

  const camelCase = (str: string): string => str.replace(/([-_]\w)/g, (g) => g[1].toUpperCase())

  let notifications: CalendarNotification[] = []
  client
    .davRequest({
      url: url,
      init: {
        method: 'GET',
        body: null,
        headers: {
          Depth: '1',
          'Content-Type': 'application/xml; charset="utf-8"',
        },
      },
      convertIncoming: false,
      parseOutgoing: false,
    })
    .then((response: DavResponse[]) => {
      console.log('get notifications detail success %o', response)
      const calendars = getCalendars()
      response.forEach((r) => {
        if (r.ok) {
          const xml = r.raw
          if (!xml) return

          const result = convert.xml2js(xml, {
            compact: true,
            trim: true,
            ignoreDeclaration: true,
            elementNameFn: (attributeName) => camelCase(attributeName.replace(/^.+:/, '')),
          }) as XmlNotificationResult

          const n = result.notification.inviteNotification
          const reply = result.notification.inviteReply
          if (n) {
            if (n.access.noAccess) {
              deleteNotification(url, (success) => {
                console.log('delete notification success %o', success)
              })
            } else {
              const comps = n.supportedCalendarComponentSet.comp
              let folderType: 'calendar' | 'todo' | null = null
              let hasCalendar = false
              let hasTodo = false
              if (Array.isArray(comps)) {
                comps.forEach((c) => {
                  if (c._attributes.name === 'VEVENT') {
                    hasCalendar = true
                  } else if (c._attributes.name === 'VTODO') {
                    hasTodo = true
                  }
                })
              } else {
                const name = comps._attributes.name
                if (name === 'VEVENT') {
                  hasCalendar = true
                } else if (name === 'VTODO') {
                  hasTodo = true
                }
              }
              if (!hasCalendar && !hasTodo) {
                return
              }
              folderType = hasCalendar ? 'calendar' : 'todo'
              const obj: CalendarNotification = {
                isReply: false,
                notiUrl: url,
                uid: n.uid._text,
                href: n.href._text,
                hostUrl: n.hosturl.href._text,
                organizer: n.organizer.commonName._text,
                summary: n.summary._text,
                folderType,
              }
              notifications.push(obj)
            }
          } else if (reply) {
            const calendar = calendars?.find((c) => c.url.endsWith(reply.hosturl.href._text))
            const obj: CalendarNotification = {
              isReply: true,
              notiUrl: url,
              replyUrl: reply.href._text,
              accept: !!reply.inviteAccepted,
              summary: reply.summary._text,
              folderType: calendar != null ? 'calendar' : 'todo',
            }
            notifications.push(obj)
          }
          console.log('get notifications detail success %o', result)
        } else {
          console.log('get notifications detail not ok %o', r)
        }
      })
      completeCallback(notifications)
      setDoingFetchNotifications(false)
    })
    .catch((error) => {
      console.log('get notifications detail failed : %o', error)
      completeCallback([])
      setDoingFetchNotifications(false)
    })
}

/**
 * Fetch all notifications from the server
 */
export const fetchNotifications = async (): Promise<void> => {
  if (doingFetchNotifications) {
    return
  }

  const client = getClient()
  if (client == null || client.account == null) {
    // 초기화 중에 호출되는 것을 방지.
    return
  }

  setDoingFetchNotifications(true)
  const notiUrl = client.account.principalUrl.replace('principals', 'notifications')
  client
    .propfind({
      url: notiUrl,
      props: {},
      depth: '0',
    })
    .then((response: DavResponse[]) => {
      const newNotifications: CalendarNotification[] = []
      let requestCount = 0
      // 공유 캘린더 초대 자동 수락 설정 (기본값 ON, false 로 명시된 경우에만 비활성화)
      const autoAcceptSharedCalendarNoti =
        settings.getStore().getState().settings.localSettings?.calendar
          ?.autoAcceptSharedCalendarNoti !== false
      response.forEach((r) => {
        if (r.ok && !notiUrl.endsWith(r.href || '')) {
          const nUrl = new URL(notiUrl)
          nUrl.pathname = r.href || ''
          console.log('get notifications success %o', nUrl.href)
          requestCount++
          getNotiDetail(nUrl.href, (notis) => {
            notis.forEach((n) => {
              if (
                !n.isReply &&
                n.folderType === 'calendar' &&
                (autoAcceptSharedCalendarNoti || isOrgSharedAccountEmail(n.organizer))
              ) {
                if (!autoAcceptingNotiUrls.has(n.notiUrl)) {
                  autoAcceptingNotiUrls.add(n.notiUrl)
                  responseNotification(n, true, () => {
                    autoAcceptingNotiUrls.delete(n.notiUrl)
                  })
                }
              } else {
                newNotifications.push(n)
              }
            })
            requestCount--
            if (requestCount === 0) {
              classInfo.notifications = newNotifications as any
              notificationEmitter.emit('notification')
            }
          })
        }
      })

      setDoingFetchNotifications(false)
    })
    .catch((error) => {
      console.log('get notifications failed : %o', error)
      setDoingFetchNotifications(false)
    })
}

/**
 * Respond to a calendar notification (accept or decline)
 *
 * @param notification - Notification object
 * @param accept - True to accept, false to decline
 * @param completeCallback - Callback with success status
 */
export const responseNotification = (
  notification: CalendarNotification,
  accept: boolean,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  const ownerPath = notification.hostUrl
  const name = notification.summary
  const xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<D:invite-reply xmlns:D="DAV:">' +
    (accept ? '<D:invite-accepted />' : '<D:invite-declined />') +
    '<D:create-in>' +
    `<D:href>${ownerPath}</D:href>` +
    '</D:create-in>' +
    `<D:comment>${name}</D:comment>` +
    '</D:invite-reply>'

  client
    .davRequest({
      url: notification.notiUrl,
      convertIncoming: false,
      init: {
        method: 'POST',
        body: xml,
      },
    })
    .then((response) => {
      console.log('response notification success %o', response)
      fetchCalendars(false, (success) => {
        if (success && accept) {
          let organizer = notification.organizer
          if (organizer && isPersonaEmail(organizer)) {
            organizer = getDisplayEmailFromPersonaEmail(organizer)
          }

          if (notification.folderType === 'calendar') {
            classInfo.calendars?.forEach((calendar) => {
              if (
                !calendar.isSync &&
                calendar.displayName === notification.summary &&
                calendar.owner === organizer
              ) {
                enableSync(calendar.url, true)
              }
            })
          }
          if (notification.folderType === 'todo') {
            setTimeout(async () => {
              // Task Folder 에 새로운 Folder가 Async로 등록이 되어서 약간 지연후 동작하도록 처리
              const { getFolders, updateFolderSync } =
                await import('pages/todo/modules/TodoFolderSync')
              getFolders().forEach((folder) => {
                if (
                  folder.isShared &&
                  folder.displayName === notification.summary &&
                  folder.owner === organizer
                ) {
                  updateFolderSync(folder.url, true)
                }
              })
            }, 200)
          }
        }
      })

      deleteNotification(notification.notiUrl, (success) => {
        completeCallback(success)
      })
    })
    .catch((error) => {
      console.log('response notification failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Delete a notification
 *
 * @param url - Notification URL
 * @param completeCallback - Callback with success status
 */
export const deleteNotification = (
  url: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  client
    .davRequest({
      url: url,
      convertIncoming: false,
      init: {
        method: 'DELETE',
        body: null,
      },
    })
    .then((response) => {
      console.log('delete notification success %o', response)
      classInfo.notifications = classInfo.notifications.filter((n) => n.notiUrl !== url)
      notificationEmitter.emit('notification')
      fetchNotifications()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('delete notification failed : %o', error)
      completeCallback(false)
    })
}
